// Review-queue classification — the SINGLE source of truth for how a SKU is
// bucketed on the Price Intelligence "Needs review" view. Defined once here and
// imported everywhere (the page counts, the status pill, the inspector) so the
// hero queue count and its three supporting counts can never drift apart.
//
// Everything is derived from data already on the page: your price, the matched-
// rival count (how many rivals price the SKU), and the rival market average.
// No API calls, no persistence — pure functions.

// "At market": prices within one cent count as equal (float-safe equality).
export const MARKET_EPSILON = 0.01

export type ReviewClass =
  | 'volumeRisk'        // you priced ABOVE the market with ≥2 rivals — you may be leaving volume on the table
  | 'marginOpportunity' // you priced BELOW the market with ≥2 rivals — you may be leaving margin on the table
  | 'limitedEvidence'   // you have a price but only ≤1 reliable rival match
  | 'competitive'       // you have a price, ≥2 rivals, and you're AT the market average
  | 'catalogGap'        // you have no price — the market carries it, you don't

export interface ClassifyInput {
  /** The operator's own price for this SKU, or null when they don't carry it. */
  yourPrice: number | null
  /** How many rivals price this SKU (productMap cell count for the SKU). */
  matchedRivalCount: number
  /** Mean of the rivals' prices — the "market" reference. */
  marketAverage: number
}

// The full classification, in one place. Order matters: no-price wins first,
// then thin evidence, then the three market-position buckets for ≥2 rivals.
export function classifySku({ yourPrice, matchedRivalCount, marketAverage }: ClassifyInput): ReviewClass {
  if (yourPrice == null) return 'catalogGap'
  if (matchedRivalCount <= 1) return 'limitedEvidence'
  const gap = yourPrice - marketAverage
  if (Math.abs(gap) < MARKET_EPSILON) return 'competitive'
  return gap > 0 ? 'volumeRisk' : 'marginOpportunity'
}

// The review queue is exactly these three classes. `competitive` and
// `catalogGap` are NOT work items and stay out of the queue.
export const QUEUE_CLASSES: ReviewClass[] = ['volumeRisk', 'marginOpportunity', 'limitedEvidence']

export function inQueue(c: ReviewClass): boolean {
  return c === 'volumeRisk' || c === 'marginOpportunity' || c === 'limitedEvidence'
}

export interface ClassCounts {
  volumeRisk: number
  marginOpportunity: number
  limitedEvidence: number
  competitive: number
  catalogGap: number
  /** Derived: the hero count. ALWAYS equals volumeRisk + marginOpportunity + limitedEvidence. */
  queue: number
}

// Tally a set of SKUs into the five classes. `queue` is derived from the three
// queue classes so it can never disagree with the supporting metrics.
export function summarize(items: ClassifyInput[]): ClassCounts {
  const c: ClassCounts = {
    volumeRisk: 0,
    marginOpportunity: 0,
    limitedEvidence: 0,
    competitive: 0,
    catalogGap: 0,
    queue: 0,
  }
  for (const it of items) c[classifySku(it)]++
  c.queue = c.volumeRisk + c.marginOpportunity + c.limitedEvidence
  return c
}
